<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import { apiErrorMessage } from '../../../../api/realEstate/errors';

export default {
  name: 'AssignedAgentsModal',
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedIds: this.project.agents.map(a => a.agent_id),
      isLoading: false,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('realEstateAgents', { agents: 'getAgents' }),
  },
  async mounted() {
    this.isLoading = true;
    try {
      await this.fetchAgents();
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions('realEstateAgents', ['fetchAgents']),
    ...mapActions('realEstate', ['setProjectAgentIds']),
    toggle(agentId) {
      this.selectedIds = this.selectedIds.includes(agentId)
        ? this.selectedIds.filter(id => id !== agentId)
        : [...this.selectedIds, agentId];
    },
    async handleSubmit() {
      this.isSubmitting = true;
      try {
        await this.setProjectAgentIds({
          propertyId: this.project.property_id,
          agentIds: this.selectedIds,
        });
        useAlert(this.$t('REAL_ESTATE.ASSIGNED_AGENTS.SUCCESS_UPDATED'));
        this.$emit('close');
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.ASSIGNED_AGENTS.ERROR_UPDATE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-md max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.ASSIGNED_AGENTS.MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <p v-if="isLoading" class="text-sm text-slate-400">
        {{ $t('REAL_ESTATE.ASSIGNED_AGENTS.LOADING') }}
      </p>
      <template v-else>
        <p v-if="!agents.length" class="text-sm text-slate-400 mb-4">
          {{ $t('REAL_ESTATE.ASSIGNED_AGENTS.NO_AGENTS') }}
        </p>
        <div v-else class="space-y-2 mb-4">
          <label
            v-for="agent in agents"
            :key="agent.agent_id"
            class="flex items-center gap-2 p-2 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="selectedIds.includes(agent.agent_id)"
              @change="toggle(agent.agent_id)"
            />
            <span class="text-sm text-slate-900 dark:text-slate-100">
              {{ agent.name }}
            </span>
          </label>
        </div>
      </template>

      <div
        class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
      >
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('REAL_ESTATE.ASSIGNED_AGENTS.CANCEL') }}
        </woot-button>
        <woot-button :is-loading="isSubmitting" @click="handleSubmit">
          {{ $t('REAL_ESTATE.ASSIGNED_AGENTS.SAVE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
